import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * "Recently viewed" product row — sections/jd-featured-collection.liquid with
 * `product_source: recently_viewed`. The visited products live in the visitor's localStorage, so the
 * server cannot know them: this element re-requests its own section through the Section Rendering
 * API on a search URL built from the stored ids. That request is a search context, so the section
 * renders from `search.results` and morphs in, cards and all — no markup is duplicated here.
 *
 * History store: Horizon already records every product view (snippets/scripts.liquid calls
 * RecentlyViewed.addProduct), but its list is capped at 4 ids — filtering out the product being
 * viewed would leave 3 cards for a row designed to show four and a half. The JD layer therefore
 * keeps its own, longer list, seeded once from Horizon's, and leaves the theme's module untouched.
 */
const STORAGE_KEY = 'jd:viewedProducts';
/* The row must be able to fill up like any featured collection, which jd-product-grid caps at 12
   cards. The product being viewed is filtered out of its own row, so the history keeps one extra id
   — otherwise the row would top out at 11 and always fall one card short of the classic one. */
const GRID_LIMIT = 12;
const MAX_PRODUCTS = GRID_LIMIT + 1;

function readHistory() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    /* First run on this browser: inherit whatever Horizon has already recorded. */
    return Array.isArray(stored) ? stored : RecentlyViewed.getProducts().map(String);
  } catch {
    return [];
  }
}

function record(productId) {
  const history = [productId, ...readHistory().filter((id) => id !== productId)].slice(0, MAX_PRODUCTS);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
  } catch {
    /* Private browsing with storage denied — the row simply stays hidden. */
  }
  return history;
}

class JdRecentlyViewed extends HTMLElement {
  async connectedCallback() {
    const sectionId = this.dataset.sectionId;
    const currentProduct = this.dataset.currentProduct;
    if (!sectionId) return;

    const history = currentProduct ? record(currentProduct) : readHistory();
    const ids = history.filter((id) => id !== currentProduct);
    if (!ids.length) return;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    /* `cache: false`: the row depends on this visitor's history, never on a shared section render. */
    await sectionRenderer.renderSection(sectionId, { url, cache: false });

    /* The search returns the products in its own relevance order — reorder the rendered cards to
       match the browsing order, which is the whole point of the row. */
    this.#sortCards(sectionId, ids);
    this.#announceSection(sectionId);
  }

  /**
   * The cards land after page load, so everything that binds on section render has already run: the
   * slider would be inert (jd-carousel.js only sets up tracks present at DOMContentLoaded) and the
   * hearts would ignore the saved wishlist. Both listen for `shopify:section:load` with the section
   * as target — the event Shopify fires when a section's markup is replaced — so replay it here.
   * Anything else binding to product cards on section load (an app, later) is covered too.
   */
  #announceSection(sectionId) {
    const section = document.getElementById(`shopify-section-${sectionId}`);
    if (!section) return;
    section.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true, detail: { sectionId } }));
  }

  #sortCards(sectionId, ids) {
    const track = document.querySelector(`#shopify-section-${sectionId} .jd-product-grid__track`);
    if (!track) return;

    /* Direct children only: `data-product-id` also sits on the wishlist button and the quick-add
       form *inside* each card, and appending one of those would pull it out of its card and turn it
       into a slide of its own. */
    const cards = new Map(
      Array.from(track.querySelectorAll(':scope > .jd-product-card[data-product-id]')).map((card) => [
        card.dataset.productId,
        card,
      ])
    );
    ids.forEach((id) => {
      const card = cards.get(id);
      if (card) track.append(card);
    });
    track.scrollTo({ left: 0, behavior: 'instant' });
  }
}

if (!customElements.get('jd-recently-viewed')) {
  customElements.define('jd-recently-viewed', JdRecentlyViewed);
}
